import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Plus, Edit2, Trash2, X, Save, Search, Home, Star, Building2, Utensils, Palmtree, Hospital, Bus, Package } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";

/**
 * POIsView — Gestión de Puntos de Interés propios de TAXIGO.
 * Los POIs con `show_in_home: true` aparecen en la sección "Destinos Rápidos"
 * de la app del pasajero. Todos los POIs activos se muestran como pines lima
 * con logo TAXIGO en el mapa del wizard.
 */
const CATEGORIES = [
  { key: "agencia",         label: "Agencia de viajes", icon: Package,   color: "#F59E0B" },
  { key: "hotel",           label: "Hotel / Lodge",     icon: Building2, color: "#3B82F6" },
  { key: "restaurant",      label: "Restaurante",       icon: Utensils,  color: "#EF4444" },
  { key: "sitio_turistico", label: "Sitio turístico",   icon: Palmtree,  color: "#10B981" },
  { key: "socio_comercial", label: "Socio comercial",   icon: Star,      color: "#C6E90C" },
  { key: "hospital",        label: "Hospital / Salud",  icon: Hospital,  color: "#DC2626" },
  { key: "transporte",      label: "Terminal / Transporte", icon: Bus,   color: "#8B5CF6" },
  { key: "otro",            label: "Otro",              icon: MapPin,    color: "#6B7280" },
];
const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

export default function POIsView() {
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    dataApi.listPOIs()
      .then(setPois)
      .catch(() => setPois([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pois.filter((p) => {
      if (filter !== "all" && p.category !== filter) return false;
      if (q && !`${p.name} ${p.address || ""}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [pois, filter, search]);

  const homeCount = pois.filter((p) => p.show_in_home).length;

  const toggleHome = async (p) => {
    try {
      await dataApi.updatePOI(p.id, { show_in_home: !p.show_in_home });
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try { await dataApi.deletePOI(p.id); load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="pois-view">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-[#3949AB]">Administración</div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            <MapPin className="w-6 h-6 text-mdd-lime" /> Puntos TAXIGO
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Puntos de Interés que aparecerán como pines lima en el mapa del pasajero. Marca "Destacado en Home" para mostrarlos en la sección de Destinos Rápidos.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowModal(true); }}
                className="mdd-btn mdd-btn-primary" data-testid="pois-new-btn">
          <Plus className="w-4 h-4" strokeWidth={2.5}/> Nuevo POI
        </button>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={MapPin} label="Total POIs"     value={pois.length} color="#3949AB" />
        <StatCard icon={Home}   label="En Home"        value={homeCount}   color="#C6E90C" />
        <StatCard icon={Star}   label="Socios comerciales" value={pois.filter(p => p.category === "socio_comercial").length} color="#F59E0B" />
        <StatCard icon={Building2} label="Hoteles"     value={pois.filter(p => p.category === "hotel").length} color="#3B82F6" />
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 max-w-md bg-white border border-neutral-300 rounded px-2.5 py-1.5">
          <Search className="w-4 h-4 text-neutral-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Buscar por nombre o dirección..."
                 className="flex-1 outline-none text-sm bg-transparent"
                 data-testid="pois-search"/>
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)}
                className="office-input max-w-[220px]"
                data-testid="pois-filter">
          <option value="all">Todas las categorías</option>
          {CATEGORIES.map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Tabla */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-neutral-500">Cargando POIs...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            {pois.length === 0 ? "No hay POIs aún. Crea el primero con el botón arriba." : "Sin resultados con los filtros."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-4 py-2.5">Categoría</th>
                <th className="px-4 py-2.5">Dirección</th>
                <th className="px-4 py-2.5">Coords</th>
                <th className="px-4 py-2.5 text-center">Home</th>
                <th className="px-4 py-2.5 text-center">Activo</th>
                <th className="px-4 py-2.5 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((p) => {
                const cat = CATEGORY_BY_KEY[p.category] || CATEGORY_BY_KEY.otro;
                const Icon = cat.icon;
                return (
                  <tr key={p.id} className="hover:bg-neutral-50 transition" data-testid={`poi-row-${p.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-neutral-900">{p.name}</div>
                      {p.description && <div className="text-[11px] text-neutral-500 truncate max-w-xs">{p.description}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-semibold"
                            style={{ backgroundColor: cat.color + "22", color: cat.color }}>
                        <Icon className="w-3 h-3"/> {cat.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-neutral-600 text-xs">{p.address || "—"}</td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-neutral-500">
                      {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button onClick={() => toggleHome(p)}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold transition ${
                                p.show_in_home
                                  ? "bg-mdd-lime text-black"
                                  : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                              }`}
                              data-testid={`poi-home-toggle-${p.id}`}>
                        <Home className="w-3 h-3"/> {p.show_in_home ? "ON" : "OFF"}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-block w-2 h-2 rounded-full ${p.is_active !== false ? "bg-emerald-500" : "bg-neutral-300"}`}/>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => { setEditing(p); setShowModal(true); }}
                              className="btn-secondary text-xs !p-1.5"
                              data-testid={`poi-edit-${p.id}`}>
                        <Edit2 className="w-3.5 h-3.5"/>
                      </button>
                      <button onClick={() => remove(p)}
                              className="btn-secondary text-xs !p-1.5 !text-red-600 ml-1"
                              data-testid={`poi-delete-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <POIModal
          poi={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 flex items-center justify-between">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{label}</div>
        <div className="text-2xl font-black text-neutral-900 mt-0.5">{value}</div>
      </div>
      <Icon className="w-6 h-6" style={{ color }} strokeWidth={2}/>
    </div>
  );
}

function POIModal({ poi, onClose, onSaved }) {
  const isEdit = !!poi;
  const [form, setForm] = useState(poi || {
    name: "", category: "hotel", lat: -12.5933, lng: -69.1893,
    address: "", description: "", phone: "", photo_url: "",
    show_in_home: false, tags: [],
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) { setErr("El nombre es obligatorio"); return; }
    if (!form.lat || !form.lng) { setErr("Coordenadas requeridas"); return; }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        category: form.category,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        address: form.address?.trim() || null,
        description: form.description?.trim() || null,
        phone: form.phone?.trim() || null,
        photo_url: form.photo_url?.trim() || null,
        show_in_home: !!form.show_in_home,
        tags: form.tags || [],
      };
      if (isEdit) await dataApi.updatePOI(poi.id, body);
      else await dataApi.createPOI(body);
      onSaved();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form onSubmit={submit}
              className="rounded-xl shadow-2xl w-full max-w-2xl fade-in max-h-[92vh] overflow-y-auto"
              style={{ backgroundColor: "#C6E90C", color: "#0A1728" }}
              data-testid="poi-modal">
          <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 z-10"
               style={{ backgroundColor: "#B5D500", borderColor: "#0A1728" }}>
            <div>
              <h3 className="font-display font-bold text-xl" style={{ color: "#0A1728" }}>
                {isEdit ? "Editar POI" : "Nuevo Punto TAXIGO"}
              </h3>
              <p className="text-xs mt-0.5" style={{ color: "#1E2A3D" }}>
                Aparecerá como pin lima en el mapa del pasajero
              </p>
            </div>
            <button type="button" onClick={onClose}
                    className="p-2 rounded transition"
                    style={{ color: "#0A1728", backgroundColor: "rgba(10,23,40,0.08)" }}>
              <X className="w-5 h-5" strokeWidth={2.5}/>
            </button>
          </div>

          <div className="p-6 space-y-3">
            <FieldPOI label="Nombre *" value={form.name} onChange={(v) => setForm({...form, name: v})} placeholder="Wasai Lodge" testid="poi-name"/>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: "#0A1728" }}>Categoría *</label>
              <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                      className="w-full rounded-md px-3 py-2.5 text-sm font-semibold"
                      style={{ backgroundColor: "#FFFFFF", color: "#0A1728", border: "1.5px solid #0A1728" }}
                      data-testid="poi-category">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldPOI label="Latitud *" value={form.lat} onChange={(v) => setForm({...form, lat: v})} placeholder="-12.5933" testid="poi-lat" type="number"/>
              <FieldPOI label="Longitud *" value={form.lng} onChange={(v) => setForm({...form, lng: v})} placeholder="-69.1893" testid="poi-lng" type="number"/>
            </div>
            <FieldPOI label="Dirección" value={form.address} onChange={(v) => setForm({...form, address: v})} placeholder="Av. Fitzcarrald 235" testid="poi-address"/>
            <FieldPOI label="Descripción" value={form.description} onChange={(v) => setForm({...form, description: v})} placeholder="Lodge premium a orillas del Tambopata..." testid="poi-description"/>
            <div className="grid grid-cols-2 gap-3">
              <FieldPOI label="Teléfono" value={form.phone} onChange={(v) => setForm({...form, phone: v})} placeholder="+51 82 572290" testid="poi-phone"/>
              <FieldPOI label="Foto (URL)" value={form.photo_url} onChange={(v) => setForm({...form, photo_url: v})} placeholder="https://..." testid="poi-photo"/>
            </div>

            {/* Toggle Show in Home */}
            <label className="flex items-center gap-3 p-3 rounded cursor-pointer"
                   style={{ backgroundColor: "rgba(10,23,40,0.08)", border: "1.5px solid #0A1728" }}>
              <input type="checkbox" checked={!!form.show_in_home}
                     onChange={(e) => setForm({...form, show_in_home: e.target.checked})}
                     className="w-5 h-5 accent-black"
                     data-testid="poi-show-in-home"/>
              <div className="flex-1">
                <div className="font-bold text-sm flex items-center gap-1.5" style={{ color: "#0A1728" }}>
                  <Home className="w-4 h-4"/> Destacar en Home del Pasajero
                </div>
                <div className="text-[11px]" style={{ color: "#1E2A3D" }}>
                  Aparecerá en la sección "Destinos Rápidos" al abrir la app.
                </div>
              </div>
            </label>

            {err && (
              <div className="rounded px-3 py-2 text-sm font-bold"
                   style={{ backgroundColor: "#0A1728", color: "#FF6B6B", border: "1px solid #FF6B6B" }}>
                {err}
              </div>
            )}
          </div>

          <div className="px-6 py-3 border-t flex justify-end gap-2 rounded-b-xl sticky bottom-0"
               style={{ borderColor: "#0A1728", backgroundColor: "#B5D500" }}>
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded font-semibold text-sm"
                    style={{ color: "#0A1728", border: "1.5px solid #0A1728", backgroundColor: "transparent" }}>
              Cancelar
            </button>
            <button type="submit" disabled={saving}
                    className="px-5 py-2 rounded font-bold text-sm disabled:opacity-40"
                    style={{ backgroundColor: "#0A1728", color: "#C6E90C" }}
                    data-testid="poi-submit">
              <Save className="w-4 h-4 inline mr-1"/> {saving ? "Guardando..." : (isEdit ? "Guardar cambios" : "Crear POI")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function FieldPOI({ label, value, onChange, placeholder, testid, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: "#0A1728" }}>{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="w-full rounded-md px-3 py-2.5 text-sm font-semibold"
             style={{ backgroundColor: "#FFFFFF", color: "#0A1728", border: "1.5px solid #0A1728" }}
             data-testid={testid}/>
    </div>
  );
}
